import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { FOCUS_RING } from '../focusRing.ts'

export interface FindingsHelpContent {
  calculation: string
  source: string
  includes: string
  excludes: string
  provisional?: string
}

/** Context stays on the existing label; hover, focus and touch share one explanation. */
export function FindingsHelp({
  label,
  content,
  children,
  className = '',
}: {
  label: string
  content: FindingsHelpContent
  children?: ReactNode
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const pinned = useRef(false)
  const hovering = useRef(false)
  const [at, setAt] = useState({ left: 8, top: 8 })

  const cancelHide = () => {
    clearTimeout(timer.current)
    timer.current = undefined
  }
  const hide = () => {
    cancelHide()
    if (panel.current?.matches(':popover-open')) panel.current.hidePopover()
    pinned.current = false
    setOpen(false)
  }
  const show = () => {
    cancelHide()
    const element = panel.current
    const anchor = trigger.current
    if (element === null || anchor === null) return
    if (!element.matches(':popover-open')) element.showPopover()
    const box = anchor.getBoundingClientRect()
    const own = element.getBoundingClientRect()
    const width = document.documentElement.clientWidth
    const height = document.documentElement.clientHeight
    setAt({
      left: Math.max(8, Math.min(box.left, width - own.width - 8)),
      top:
        box.bottom + own.height + 8 > height
          ? Math.max(8, box.top - own.height - 4)
          : box.bottom + 4,
    })
    setOpen(true)
  }
  const scheduleHide = () => {
    if (pinned.current || hovering.current || document.activeElement === trigger.current) return
    cancelHide()
    // A short bridge lets the pointer cross the gap to the hoverable tooltip.
    timer.current = setTimeout(hide, 180)
  }

  useEffect(() => () => clearTimeout(timer.current), [])
  useEffect(() => {
    if (!open) return undefined
    const dismiss = (event: PointerEvent) => {
      const target = event.target as Node
      if (!panel.current?.contains(target) && !trigger.current?.contains(target)) hide()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide()
    }
    document.addEventListener('pointerdown', dismiss)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', hide)
    return () => {
      document.removeEventListener('pointerdown', dismiss)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', hide)
    }
  }, [open])

  return (
    <span className="relative inline-flex min-w-0">
      <button
        ref={trigger}
        type="button"
        aria-label={children === undefined ? `Detalhes: ${label}` : undefined}
        aria-describedby={open ? panelId : undefined}
        aria-expanded={open}
        onPointerEnter={(event) => {
          if (event.pointerType !== 'touch') {
            hovering.current = true
            show()
          }
        }}
        onPointerLeave={() => {
          hovering.current = false
          scheduleHide()
        }}
        onFocus={show}
        onBlur={() => {
          pinned.current = false
          scheduleHide()
        }}
        onClick={() => {
          if (pinned.current) hide()
          else {
            pinned.current = true
            show()
          }
        }}
        className={`cursor-help rounded-sm text-left decoration-stroke-strong underline-offset-4 hover:underline ${FOCUS_RING} ${className}`}
      >
        {children ?? label}
      </button>
      <div
        ref={panel}
        popover="manual"
        id={panelId}
        role="tooltip"
        aria-hidden={!open}
        onPointerEnter={() => {
          hovering.current = true
          cancelHide()
        }}
        onPointerLeave={() => {
          hovering.current = false
          scheduleHide()
        }}
        className="bg-background-raised border-stroke-strong text-tSmall text-text-strong m-0 max-h-[calc(100vh-1rem)] w-[min(22rem,calc(100vw-1rem))] overflow-y-auto rounded-base border p-3 shadow-lg"
        style={{ position: 'fixed', left: at.left, top: at.top, right: 'auto', bottom: 'auto' }}
      >
        <div className="flex flex-col gap-2">
          <span>
            <strong>Cálculo:</strong> {content.calculation}
          </span>
          <span>
            <strong>Fonte:</strong> {content.source}
          </span>
          <span>
            <strong>Inclui:</strong> {content.includes}
          </span>
          <span>
            <strong>Exclui:</strong> {content.excludes}
          </span>
          {content.provisional !== undefined && <span>{content.provisional}</span>}
        </div>
      </div>
    </span>
  )
}
